import { Check, Eye, EyeOff } from "lucide-react"
import { useEffect, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { AutocompleteInput } from "@/components/forms/autoCompleteInput"
import { CustomInput } from "@/components/forms/customInput"
import { Button } from "@/components/ui/button"
import { useAuthForm } from "@/features/auth/hooks/useAuthForm"
import type { GoogleSignupFormData, SignupFormData } from "@/features/auth/schemas/auth.schema"
import { googleSignupSchema } from "@/features/auth/schemas/auth.schema"
import { useAuthStore } from "@/features/auth/store/authStore"
import { useTranslation } from "@/language"

interface GoogleSignupData {
  name: string
  email: string
  username: string
}

export function MobileSignupScreen() {
  const navigate = useNavigate()
  const { username } = useParams<{ username: string }>()
  const { t } = useTranslation(["profile", "common"])
  const signup = useAuthStore(state => state.signup)

  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [isGoogleSignup, setIsGoogleSignup] = useState(false)

  const handleSignupSubmit = async (data: SignupFormData | GoogleSignupFormData) => {
    if (isGoogleSignup) {
      try {
        await signup({ ...data, googleVerified: true } as SignupFormData & {
          googleVerified: boolean
        })
        navigate("/onboarding/permissions")
      } catch (error) {
        console.error("[MobileSignup] signup error:", error)
      }
    } else {
      if (!data.password || data.password.length < 8) {
        form.setError("password", {
          type: "manual",
          message: "Password must be at least 8 characters",
        })
        return
      }
      await signup(data as SignupFormData)
      navigate("/verify-email", { state: { email: data.email } })
    }
  }

  const { form, isSubmitting, handleSubmit } = useAuthForm<GoogleSignupFormData>(
    googleSignupSchema,
    handleSignupSubmit as (data: GoogleSignupFormData) => Promise<void>,
  )

  useEffect(() => {
    const googleDataStr = sessionStorage.getItem("google_signup")
    if (googleDataStr) {
      try {
        const googleData: GoogleSignupData = JSON.parse(googleDataStr)
        form.reset({
          name: googleData.name || "",
          username: googleData.username || "",
          email: googleData.email || "",
          password: "",
        })
        setIsGoogleSignup(true)
        sessionStorage.removeItem("google_signup")
      } catch {
        sessionStorage.removeItem("google_signup")
      }
    }
  }, [form])

  useEffect(() => {
    if (username) {
      form.setValue("referralCode", username.toUpperCase())
    }
  }, [username, form])

  return (
    <div className="space-y-7.5">
      <div className="text-center mb-7.5">
        <h2 className="text-xl leading-normal font-semibold text-primary">
          {t("createProfile.title")}
        </h2>
      </div>

      <form className="space-y-3.75">
        <CustomInput
          label="Name"
          type="text"
          placeholder="Name"
          required={true}
          {...form.register("name")}
          error={form.formState.errors.name?.message as string | undefined}
          disabled={isSubmitting}
          onClearError={() => form.clearErrors("name")}
        />

        <CustomInput
          label="Username"
          placeholder="Username"
          type="text"
          required={true}
          {...form.register("username")}
          error={form.formState.errors.username?.message as string | undefined}
          disabled={isSubmitting}
          onClearError={() => form.clearErrors("username")}
        />

        <AutocompleteInput
          label="City"
          mode="city"
          value={form.watch("city") || ""}
          placeholder="City"
          onLocationSelect={locationData => {
            form.setValue("city", locationData.city || "")
            form.setValue("state", locationData.state || "")
            form.setValue("country", locationData.country || "")
            form.setValue("zipcode", locationData.zipCode || "")
            form.setValue("latitude", locationData.lat)
            form.setValue("longitude", locationData.lng)
          }}
          error={form.formState.errors.city?.message as string | undefined}
          onClearError={() => form.clearErrors("city")}
          disabled={isSubmitting}
          required={true}
        />

        <CustomInput
          label="Email Address"
          type="email"
          placeholder="Email"
          required={true}
          {...form.register("email")}
          error={form.formState.errors.email?.message as string | undefined}
          disabled={isSubmitting || isGoogleSignup}
          readOnly={isGoogleSignup}
          onClearError={() => form.clearErrors("email")}
        />

        {!isGoogleSignup && (
          <>
            <div className="relative">
              <CustomInput
                label="Password"
                placeholder="Password"
                type={showPassword ? "text" : "password"}
                required={true}
                {...form.register("password")}
                error={form.formState.errors.password?.message as string | undefined}
                disabled={isSubmitting}
                onClearError={() => form.clearErrors("password")}
                className="pr-8"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-1 top-[70%] -translate-y-1/2 text-white hover:text-gray-300 transition-colors"
                tabIndex={-1}
              >
                {showPassword ? <EyeOff size={17} /> : <Eye size={17} />}
              </button>
            </div>

            <div className="relative">
              <CustomInput
                label="Confirm Password"
                placeholder="Confirm Password"
                type={showConfirmPassword ? "text" : "password"}
                required={true}
                {...form.register("confirmPassword")}
                error={form.formState.errors.confirmPassword?.message as string | undefined}
                disabled={isSubmitting}
                onClearError={() => form.clearErrors("confirmPassword")}
                className="pr-8"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-1 top-[70%] -translate-y-1/2 text-white hover:text-gray-300 transition-colors"
                tabIndex={-1}
              >
                {showConfirmPassword ? <EyeOff size={17} /> : <Eye size={17} />}
              </button>
            </div>
          </>
        )}

        <div className="relative">
          <CustomInput
            label="Have a Referral Code?"
            placeholder="Referral Code"
            type="text"
            required={false}
            {...form.register("referralCode")}
            error={form.formState.errors.referralCode?.message as string | undefined}
            disabled={isSubmitting}
            onClearError={() => form.clearErrors("referralCode")}
            className="pr-8"
          />
          {form.watch("referralCode") && (
            <Check size={17} className="absolute right-1 top-[70%] -translate-y-1/2 text-white" />
          )}
        </div>
      </form>

      <div className="space-y-5">
        <Button
          type="button"
          onClick={handleSubmit}
          variant="primary"
          size="md"
          className="rounded-full uppercase py-3.75 h-12.5 text-sm font-semibold"
          isLoading={isSubmitting}
          disabled={isSubmitting}
          fullWidth
        >
          {t("buttons.continue", { ns: "common" })}
        </Button>

        <p className="text-sm leading-normal font-semibold text-center text-[#F2F0EBE6]">
          Already have an account?
        </p>

        <Button
          type="button"
          onClick={() => navigate("/login")}
          variant="outline"
          size="md"
          className="text-sm font-semibold rounded-full uppercase border-white py-3.75 h-12.5"
          fullWidth
        >
          Log in
        </Button>
      </div>
    </div>
  )
}
